<template>
  <v-container flat fluid>
    <v-dialog v-model="dialogs.template" max-width="800">
      <obj-template
        v-if="dialogs.template"
        :template="template"
        :newObj="template == null"
        :type="'Client'"
        v-on:close="dialogs.template = false"
      ></obj-template>
    </v-dialog>

    <v-form>
      <v-row>
        <v-col class="py-0">
          <v-expansion-panels v-model="panel" multiple>
            <v-expansion-panel>
              <v-expansion-panel-header
                :class="panel.includes(0) ? 'primary active-panel' : ''"
                >Client Details</v-expansion-panel-header
              >
              <v-expansion-panel-content>
                <v-row class="pt-4">
                  <v-col cols="12" sm="8" md="6" lg="5" xl="4">
                    <v-select
                      v-model="client.template"
                      :items="templateItems"
                      item-text="name"
                      label="Template"
                      placeholder="Select a client template"
                      outlined
                      filled
                      hide-details
                      return-object
                      no-data-text="Create your first template above"
                      clearable
                    >
                      <template v-slot:prepend-item>
                        <v-subheader>
                          New client Template
                          <v-spacer></v-spacer>
                          <v-btn
                            x-small
                            @click="newTemplate()"
                            fab
                            color="blue"
                          >
                            <v-icon color="white">{{ 'mdi-plus' }}</v-icon>
                          </v-btn>
                        </v-subheader>
                      </template>
                      <template v-slot:item="{ item }">
                        <span>{{ item.name }}</span>
                        <v-spacer></v-spacer>
                        <v-list-item-action @click.stop>
                          <v-btn icon @click="editTemplate(item)">
                            <v-icon>{{ 'mdi-pencil' }}</v-icon>
                          </v-btn>
                        </v-list-item-action>
                      </template>
                    </v-select>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-subheader>
                      Company
                      <v-spacer></v-spacer>
                    </v-subheader>
                    <v-divider class="mb-4"></v-divider>
                    <v-row>
                      <v-col cols="12" class="py-0">
                        <v-text-field
                          v-model="client.name"
                          label="Company Name"
                          outlined
                          filled
                          autocomplete="off"
                          :rules="[rules.required]"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="6" class="py-0">
                        <v-text-field
                          v-model="client.paymentTerms"
                          type="number"
                          label="Payment Terms (days)"
                          outlined
                          filled
                          autoscomplete="off"
                          :rules="[rules.required]"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="6" class="py-0">
                        <date
                          v-model="client.registered"
                          label="Registered Date"
                          :year="true"
                          :max="new Date().toISOString().substr(0, 10)"
                        ></date>
                      </v-col>
                    </v-row>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-subheader>
                      Invoice Address
                      <v-spacer></v-spacer>
                    </v-subheader>
                    <v-divider class="mb-4"></v-divider>
                    <find-address :addressObj="client.address"></find-address>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" class="py-0">
                    <v-subheader>
                      Sites
                      <v-spacer></v-spacer>
                      <v-btn
                        fab
                        x-small
                        color="blue"
                        @click="newSite"
                        :disabled="!canAddSite"
                        :dark="canAddSite"
                      >
                        <font-awesome-icon size="lg" icon="plus" />
                      </v-btn>
                    </v-subheader>
                    <v-divider class="mb-4"></v-divider>
                    <v-row
                      v-for="(site, index) in client.sites"
                      :key="index"
                      class="pt-0"
                    >
                      <v-col cols="12" md="6" class="py-0">
                        <v-text-field
                          v-model="site.name"
                          label="Site Name"
                          outlined
                          filled
                          autocomplete="off"
                          :rules="[rules.required]"
                        >
                          <template v-slot:append-outer>
                            <v-tooltip top color="grey">
                              <template v-slot:activator="{ on }">
                                <v-btn
                                  v-on="on"
                                  small
                                  icon
                                  color="grey"
                                  @click="removeSite(index)"
                                >
                                  <font-awesome-icon
                                    size="lg"
                                    icon="minus-circle"
                                  />
                                </v-btn>
                              </template>
                              <span>Remove Site</span>
                            </v-tooltip>
                          </template>
                        </v-text-field>
                        <find-address :addressObj="site.address"></find-address>
                      </v-col>
                      <v-col cols="12" md="6" class="py-0">
                        <v-subheader class="mt-5">
                          Contacts
                          <v-spacer></v-spacer>
                          <v-btn
                            fab
                            x-small
                            color="blue"
                            @click="newContact(site)"
                            :disabled="!canAddContact(site)"
                            :dark="canAddContact(site)"
                          >
                            <font-awesome-icon size="lg" icon="plus" />
                          </v-btn>
                        </v-subheader>
                        <v-divider class="mb-4"></v-divider>
                        <v-row
                          v-for="(contact, index) in site.contacts"
                          :key="index"
                        >
                          <v-col cols="12" sm="6" class="py-0">
                            <v-text-field
                              v-model="contact.name"
                              label="Name"
                              outlined
                              filled
                              autocomplete="off"
                              :rules="contact.name ? [rules.name] : []"
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" sm="6" class="py-0">
                            <v-text-field
                              v-model="contact.description"
                              label="Department"
                              outlined
                              filled
                              autocomplete="off"
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" sm="6" md="5" class="py-0">
                            <v-select
                              v-model="contact.type"
                              :items="contactItems"
                              label="Type"
                              outlined
                              filled
                            ></v-select>
                          </v-col>
                          <v-col cols="12" sm="6" md="7" class="py-0">
                            <v-text-field
                              v-if="contact.type == 'Phone'"
                              v-model="contact.value"
                              label="Value"
                              outlined
                              :readonly="contact.type == null"
                              filled
                              autocomplete="off"
                              :rules="
                                contact.value ? [rules.phone] : [rules.required]
                              "
                              v-mask="phoneMask"
                            >
                              <template v-slot:append-outer>
                                <v-tooltip top color="grey">
                                  <template v-slot:activator="{ on }">
                                    <v-btn
                                      v-on="on"
                                      small
                                      icon
                                      color="grey"
                                      @click="removeContact(site, index)"
                                    >
                                      <font-awesome-icon
                                        size="lg"
                                        icon="minus-circle"
                                      />
                                    </v-btn>
                                  </template>
                                  <span>Remove Contact</span>
                                </v-tooltip>
                              </template>
                            </v-text-field>
                            <v-text-field
                              v-else
                              v-model="contact.value"
                              label="Value"
                              outlined
                              :readonly="contact.type == null"
                              filled
                              autocomplete="off"
                              :rules="
                                contact.value ? [rules.email] : [rules.required]
                              "
                            >
                              <template v-slot:append-outer>
                                <v-tooltip top color="grey">
                                  <template v-slot:activator="{ on }">
                                    <v-btn
                                      v-on="on"
                                      small
                                      icon
                                      color="grey"
                                      @click="removeContact(site, index)"
                                    >
                                      <font-awesome-icon
                                        size="lg"
                                        icon="minus-circle"
                                      />
                                    </v-btn>
                                  </template>
                                  <span>Remove Contact</span>
                                </v-tooltip>
                              </template>
                            </v-text-field>
                          </v-col>
                        </v-row>
                      </v-col>
                      <v-col cols="12">
                        <v-divider class="mb-4"></v-divider>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header
                :class="panel.includes(1) ? 'primary active-panel' : ''"
                >Comments</v-expansion-panel-header
              >
              <v-expansion-panel-content>
                <v-row justify="center">
                  <v-col cols="12" justify-self="center">
                    <v-textarea
                      v-model="client.comments"
                      outlined
                      filled
                      label="Comments"
                      placeholder="General comments"
                    ></v-textarea>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex';

import rules from '@/rules.js';
import findAddress from '@/components/address.vue';
import objTemplate from '@/components/template';
import date from '@/components/date';

export default {
  props: {
    client: Object
  },
  components: {
    findAddress,
    objTemplate,
    date
  },
  data() {
    return {
      panel: [0, 1],
      contactItems: ['Phone', 'Email'],
      phoneMask: '###########',
      dialogs: {
        template: false
      },
      template: null,
      dobMenu: false,
      rules: rules
    };
  },
  computed: {
    ...mapState({
      clients: state => state.clients,
      templates: state => state.templates
    }),
    templateItems() {
      return this.templates.items.filter(template => {
        return !template.predefined && template.type == 'Client';
      });
    },
    canAddSite() {
      return this.canAddToArray(this.client.sites, ['name']);
    }
  },
  watch: {
    client(val) {
      this.checkForData();
    }
  },
  methods: {
    checkForData() {
      if (!this.client.sites) {
        this.$set(this.client, 'sites', []);
        this.newSite();
      }

      if (!this.client.address) {
        this.$set(this.client, 'address', {
          address: null,
          postcode: null,
          city: null
        });
      }

      if (!this.client.paymentTerms) {
        this.$set(this.client, 'paymentTerms', 28);
      }
    },
    canAddContact(site) {
      return this.canAddToArray(site.contacts, ['name']);
    },
    newContact(site) {
      site.contacts.push({
        name: null,
        department: null,
        type: null,
        value: null
      });
    },
    removeContact(site, index) {
      site.contacts.splice(index, 1);
    },
    newSite() {
      this.client.sites.push({
        name: null,
        address: {
          address: null,
          postcode: null,
          city: null
        },
        contacts: [{}]
      });
    },
    removeSite(index) {
      this.client.sites.splice(index, 1);
    },
    newTemplate() {
      this.template = null;
      this.dialogs.template = true;
    },
    editTemplate(template) {
      this.template = template;
      this.dialogs.template = true;
    }
  },
  mounted() {
    this.checkForData();
  }
};
</script>
